import React, { useCallback, useEffect, useState } from 'react';
import { Stack, Text } from '@adc/parallax-component-library';
import { RichText } from 'Components/utility';
import { SHOW_MODAL } from 'Reducers/modal/types';
import { getDifferenceInMinutesFromNow } from 'Utilities/dateHelpers';
import i18n from 'Utilities/i18n';
import { logout } from 'Utilities/session';
import { getObject, removeObject, setObject } from 'Utilities/storage';
import { store } from 'Utilities/store';
const useAuth = () => {
    const [isLocked, setIsLocked] = useState(false);
    const [lockoutMessage, setLockoutMessage] = useState('');
    const [lockoutSubmessage, setlockoutSubmessage] = useState('');
    const handleLockoutError = (error) => {
        const attemptsResetAfter = Number(error.response?.headers['x-attempts-reset-after']);
        const attemptsResetAt = Number(error.response?.headers['x-attempts-reset-at']);
        showLockoutMessage(attemptsResetAt, attemptsResetAfter);
    };
    const showLockoutMessage = useCallback((attemptsResetAt, attemptsResetAfter) => {
        const { time, difference } = getDifferenceInMinutesFromNow(attemptsResetAt);
        if (difference < 0) {
            return;
        }
        setIsLocked(true);
        setLockoutMessage(i18n.t('Login.modals.signInErrors.accountLocked'));
        setlockoutSubmessage(i18n.t('Login.modals.signInErrors.accountLockedPeriod', { time }));
        const lockoutInterval = setInterval(() => {
            const { time, difference } = getDifferenceInMinutesFromNow(attemptsResetAt);
            if (difference < 0) {
                clearInterval(lockoutInterval);
                setLockoutMessage('');
                setlockoutSubmessage('');
                setIsLocked(false);
                removeObject('loginLockout');
            }
            else {
                setlockoutSubmessage(i18n.t('Login.modals.signInErrors.accountLockedPeriod', { time }));
            }
        }, 1000);
        setObject('loginLockout', {
            attemptsResetAt,
            lockoutInterval,
            attemptsResetAfter,
        });
    }, []);
    useEffect(() => {
        const loginLockout = getObject('loginLockout');
        if (loginLockout) {
            showLockoutMessage(loginLockout.attemptsResetAt, loginLockout.attemptsResetAfter);
        }
        return () => {
            const loginLockout = getObject('loginLockout');
            if (loginLockout) {
                clearInterval(loginLockout.lockoutInterval);
            }
        };
    }, [showLockoutMessage]);
    const showLoginFailure = useCallback((attemptsRemaining = -1, attemptsResetAfter, attemptsResetAt, onCloseAfterLockout, onClose) => {
        const modalProps = {
            details: (React.createElement(Stack, { gap: 4 },
                React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "Login.modals.signInErrors.loginFailure", testID: "Login.modals.signInErrors.loginFailure", marginBottom: "$1" }, i18n.t('Login.modals.signInErrors.loginFailure')),
                attemptsRemaining >= 0 ? (React.createElement(Stack, { display: "inline" },
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "Login.modals.signInErrors.attemptsRemaining", testID: "Login.modals.signInErrors.attemptsRemaining" }, i18n.t('Login.modals.signInErrors.attemptsRemaining', {
                        count: attemptsRemaining,
                    })),
                    attemptsResetAfter ? (React.createElement(Text, { color: "$text.100", type: "$bodyBase.medium", nativeID: "Login.modals.signInErrors.accountWillBeLocked", testID: "Login.modals.signInErrors.accountWillBeLocked" }, i18n.t('Login.modals.signInErrors.accountWillBeLocked', {
                        time: attemptsResetAfter,
                        duration: attemptsResetAfter,
                    }))) : null)) : null)),
            testID: 'Auth',
        };
        modalProps.onClose = () => {
            onClose && onClose();
        };
        if (attemptsResetAfter && attemptsResetAt) {
            modalProps.onClose = () => {
                showLockoutMessage(attemptsResetAt, attemptsResetAfter);
                onCloseAfterLockout && onCloseAfterLockout();
            };
        }
        store.dispatch({
            type: SHOW_MODAL,
            modalProps,
        });
    }, [showLockoutMessage]);
    const handleLoginError = (error, onCloseAfterLockout, onClose) => {
        if (error.response?.data.details === 'Authorization.isRequired') {
            logout();
            return;
        }
        if (error.response && error.response.headers['x-attempts-remaining']) {
            const attemptsRemaining = Number(error.response.headers['x-attempts-remaining']);
            const attemptsResetAt = error.response.headers['x-attempts-reset-at']
                ? Number(error.response.headers['x-attempts-reset-at'])
                : 0;
            const attemptsResetAfter = error.response.headers['x-attempts-reset-after']
                ? Number(error.response.headers['x-attempts-reset-after']) / 60
                : 0;
            showLoginFailure(attemptsRemaining, attemptsResetAfter, attemptsResetAt, onCloseAfterLockout, onClose);
            return;
        }
        showLoginFailure();
    };
    const showHcpError = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: 4, nativeID: "Global.modals.blockHCPUser.body" },
                    React.createElement(RichText, { text: i18n.t('Global.modals.blockHCPUser.body', {
                            appName: i18n.t('Global.appNames.libreView'),
                        }) }))),
                testID: 'Auth',
            },
        });
    }, []);
    const showForgotPasswordBlockedMessage = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: 4 },
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "ForgotPassword.modals.forgotErrors.youReached", marginBottom: "$1" }, `${i18n.t('ForgotPassword.modals.forgotErrors.youReached')}`),
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "ForgotPassword.modals.forgotErrors.consider", marginBottom: "$1" }, `${i18n.t('ForgotPassword.modals.forgotErrors.consider')}`))),
                testID: 'Auth',
            },
        });
    }, []);
    const showForgotPasswordConfirmatioMessage = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: 4 },
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "ForgotPassword.modals.forgotErrors.sentInstructions", marginBottom: "$1" }, i18n.t('ForgotPassword.modals.forgotErrors.sentInstructions')),
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "ForgotPassword.modals.forgotErrors.notSeeingEmail", marginBottom: "$1" }, i18n.t('ForgotPassword.modals.forgotErrors.notSeeingEmail')))),
                testID: 'Auth',
            },
        });
    }, []);
    const showCountryRegionError = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: i18n.t('Login.modals.signInErrors.regionDoesNotMatch'),
                testID: 'Auth',
            },
        });
    }, []);
    return {
        handleLoginError,
        handleLockoutError,
        showCountryRegionError,
        showHcpError,
        isLocked,
        lockoutMessage,
        lockoutSubmessage,
        showForgotPasswordBlockedMessage,
        showForgotPasswordConfirmatioMessage,
    };
};
export default useAuth;
